import { type ReactNode, useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { backendApis } from '@/backendApis';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { useSubscription } from '@/features/subscription/subscription.query';
import { trackEvent } from '@/utils/analytics';

import { REFERRAL_CREDIT } from './referral.constants';

export function ReferralCodeSection({ footer }: { footer?: ReactNode }) {
  const { status, refetch } = useSubscription();
  const [code, setCode] = useState('');
  const [isApplying, setIsApplying] = useState(false);

  const handleApply = async () => {
    if (!code.trim()) return;

    setIsApplying(true);
    try {
      const response = await backendApis.referral.apply({
        referral_code: code.trim(),
      });
      if (response.success) {
        toast.success(response.message);
        trackEvent('REFERRAL_CODE_APPLIED', {
          referral_code: code.trim(),
          experiment_variant: response.reward_type ?? 'payment_based',
        });
        setCode('');
        refetch();
      }
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { data?: { detail?: string; message?: string } };
      };
      toast.error(
        axiosError?.response?.data?.detail ||
          axiosError?.response?.data?.message ||
          'Failed to apply referral code.'
      );
    } finally {
      setIsApplying(false);
    }
  };

  if (status.referral_status === 'successful') {
    return footer ? (
      <YStack className="bg-surface border border-border dark:border-border-strong rounded-xl overflow-hidden">
        {footer}
      </YStack>
    ) : null;
  }

  if (status.has_applied_referral) {
    const isActivity = status.referral_reward_type === 'activity_based';
    return (
      <YStack className="bg-surface border border-success-5 dark:border-success-12 rounded-xl overflow-hidden">
        <XStack className="items-center gap-3 py-4 px-4">
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-success-9 text-success-1">
            <Icons.check className="w-3 h-3" />
          </span>
          <YStack className="gap-0.5">
            <span className="text-sm font-medium text-success-11 dark:text-success-9">
              Referral code applied
            </span>
            <span className="text-sm text-foreground-muted">
              {isActivity
                ? `Use LittleBird for 3 days to earn a ${REFERRAL_CREDIT.ACTIVITY} credit. No credit card required.`
                : `After your 14-day free trial and first paid month, you'll get ${REFERRAL_CREDIT.PAYMENT} in credits. That's 2 months free.`}
            </span>
          </YStack>
        </XStack>
        {footer && (
          <div className="border-t border-border dark:border-border-strong">
            {footer}
          </div>
        )}
      </YStack>
    );
  }

  return (
    <YStack className="bg-surface border border-border dark:border-border-strong rounded-xl overflow-hidden">
      <YStack className="py-5 px-6 gap-4">
        <XStack className="items-center justify-between gap-6">
          <YStack className="gap-0.5">
            <span className="text-sm font-medium text-foreground">
              Have a referral code?
            </span>
            <p className="text-sm text-foreground-muted">
              Enter a code to unlock referral rewards
            </p>
          </YStack>
        </XStack>
        <XStack className="gap-3">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Enter referral code"
            className="flex-1"
            onKeyDown={(e) => e.key === 'Enter' && handleApply()}
          />
          <Button
            onClick={handleApply}
            disabled={!code.trim() || isApplying}
            isLoading={isApplying}
            size="default"
          >
            Apply
          </Button>
        </XStack>
      </YStack>
      {footer && (
        <div className="border-t border-border dark:border-border-strong">
          {footer}
        </div>
      )}
    </YStack>
  );
}
